import { useMemo } from 'react';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
  type ChartOptions,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import { etiquetaMes, formatearCLPCompacto } from '@lexicore/shared';
import { useTema } from '../contextos/TemaContext';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Filler, Tooltip, Legend);

/**
 * Los gráficos leen los colores del tema en cada render.
 *
 * Chart.js pinta sobre canvas, así que no hereda las variables CSS: si el tema
 * cambia hay que volver a construir las opciones. Por eso `useTema()` está aquí
 * — es lo que hace que las etiquetas y la rejilla acompañen al interruptor en
 * vez de quedarse grises sobre fondo blanco.
 */
function usePaleta() {
  const { tema } = useTema();

  return useMemo(() => {
    const oscuro = tema === 'dark';
    return {
      texto: oscuro ? '#9898b0' : '#4b5563',
      rejilla: oscuro ? '#2a2a40' : '#e5e7eb',
      fondoTooltip: oscuro ? '#222236' : '#ffffff',
      bordeTooltip: oscuro ? '#3a3a55' : '#d1d5db',
      textoTooltip: oscuro ? '#f0f0f5' : '#111827',
      azul: oscuro ? '#2196F3' : '#2563eb',
      verde: oscuro ? '#00C853' : '#059669',
      rojo: '#D32F2F',
      amarillo: oscuro ? '#FFC107' : '#d97706',
    };
  }, [tema]);
}

function opcionesBase(paleta: ReturnType<typeof usePaleta>): ChartOptions<'line' | 'bar'> {
  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: {
        labels: { color: paleta.texto, font: { family: 'Inter, sans-serif', size: 11 }, boxWidth: 10, boxHeight: 10, usePointStyle: true },
      },
      tooltip: {
        backgroundColor: paleta.fondoTooltip,
        borderColor: paleta.bordeTooltip,
        borderWidth: 1,
        titleColor: paleta.textoTooltip,
        bodyColor: paleta.textoTooltip,
        padding: 10,
        titleFont: { family: 'Inter, sans-serif', size: 12 },
        bodyFont: { family: 'JetBrains Mono, monospace', size: 11 },
      },
    },
    scales: {
      x: {
        grid: { color: paleta.rejilla, drawTicks: false },
        ticks: { color: paleta.texto, font: { family: 'Inter, sans-serif', size: 10 } },
        border: { color: paleta.rejilla },
      },
      y: {
        grid: { color: paleta.rejilla, drawTicks: false },
        ticks: {
          color: paleta.texto,
          font: { family: 'JetBrains Mono, monospace', size: 10 },
          callback: (valor) => formatearCLPCompacto(Number(valor)),
        },
        border: { color: paleta.rejilla },
      },
    },
  };
}

export function GraficoTendencia({ datos }: { datos: { mes: string; adjudicado: number; costos: number; cobrado: number }[] }) {
  const paleta = usePaleta();

  return (
    <div style={{ height: 240 }}>
      <Line
        data={{
          labels: datos.map((punto) => etiquetaMes(punto.mes)),
          datasets: [
            {
              label: 'Adjudicado',
              data: datos.map((punto) => punto.adjudicado),
              borderColor: paleta.azul,
              backgroundColor: 'rgba(33,150,243,.15)',
              fill: true,
              tension: 0.3,
              pointRadius: 3,
              borderWidth: 2.5,
            },
            {
              label: 'Cobrado',
              data: datos.map((punto) => punto.cobrado),
              borderColor: paleta.verde,
              backgroundColor: 'transparent',
              tension: 0.3,
              pointRadius: 3,
              borderWidth: 2,
            },
            {
              label: 'Costos',
              data: datos.map((punto) => punto.costos),
              borderColor: paleta.rojo,
              backgroundColor: 'transparent',
              tension: 0.3,
              pointRadius: 3,
              borderWidth: 2,
              borderDash: [4, 3],
            },
          ],
        }}
        options={opcionesBase(paleta) as ChartOptions<'line'>}
      />
    </div>
  );
}

export function GraficoBarras({
  etiquetas,
  series,
  horizontal,
  altura = 240,
}: {
  etiquetas: string[];
  series: { nombre: string; datos: number[]; color: string }[];
  horizontal?: boolean;
  altura?: number;
}) {
  const paleta = usePaleta();
  const opciones = opcionesBase(paleta) as ChartOptions<'bar'>;

  return (
    <div style={{ height: altura }}>
      <Bar
        data={{
          labels: etiquetas,
          datasets: series.map((serie) => ({
            label: serie.nombre,
            data: serie.datos,
            backgroundColor: serie.color,
            borderRadius: 3,
            maxBarThickness: 42,
          })),
        }}
        options={{
          ...opciones,
          indexAxis: horizontal ? ('y' as const) : ('x' as const),
          plugins: {
            ...opciones.plugins,
            legend: { ...opciones.plugins?.legend, display: series.length > 1 },
          },
        }}
      />
    </div>
  );
}
